import type { ReactNode } from 'react';
import { CircleAlert, ScrollText } from 'lucide-react';
import { Card, EmptyState, LoadMore, TableSkeleton } from '@/components/ui';
import { useSiteActivity } from '@/hooks/useSites';
import { errorText } from '@/utils/errors';
import { formatDateTime } from '@/utils/format';
import { auditActionLabel } from '@/utils/labels';
import { SEO_CHANGE_FIELDS } from '@/utils/seo';

const TRACE_COUNTS: Record<string, string> = {
  verification: 'коды подтверждения',
  counters: 'счётчики',
  hreflang: 'hreflang',
  canonical: 'canonical',
  files: 'файлы подтверждения',
  connections: 'подключения аналитики',
  keptCounters: 'оставлено в больших скриптах',
};

const quote = (value: unknown) => (value === null || value === undefined || value === '' ? '—' : `«${String(value)}»`);

/** The details the site's own events carry, as a line. */
function detailsOf(action: string, details: Record<string, unknown> | null | undefined): string | null {
  if (!details) return null;
  if (action === 'seo.changed' && Array.isArray(details['changes'])) {
    return (details['changes'] as { field: string; before: unknown; after: unknown }[])
      .map((change) => `${SEO_CHANGE_FIELDS[change.field] ?? change.field}: ${quote(change.before)} → ${quote(change.after)}`)
      .join('; ');
  }
  if (action === 'site.clone.traces_cleared') {
    const parts = Object.entries(details).filter(([, value]) => typeof value === 'number' && value > 0).map(([key, value]) => `${TRACE_COUNTS[key] ?? key}: ${String(value)}`);
    return parts.length > 0 ? parts.join(', ') : 'ничего не нашлось';
  }
  return null;
}

/**
 * A site's activity from the audit trail (S1): who asked for what and how it
 * ended. Unlike the History tab, which shows jobs step by step, this outlives
 * the jobs — they are deleted after a week, the trail is kept for a year.
 */
export function ActivityTab({ siteId }: { readonly siteId: string }): ReactNode {
  const query = useSiteActivity(siteId);

  if (query.isPending) {
    return (
      <Card flush>
        <TableSkeleton rows={5} columns={4} />
      </Card>
    );
  }

  if (query.isError) {
    return (
      <Card>
        <p className="field-error" role="alert">
          <CircleAlert size={14} aria-hidden="true" />
          {errorText(query.error, 'Журнал не загрузился')}
        </p>
      </Card>
    );
  }

  const events = query.data.pages.flatMap((page) => page.items);

  return (
    <>
      <Card flush>
        {events.length === 0 ? (
          <EmptyState icon={ScrollText} message="В журнале об этом сайте пока ничего нет" />
        ) : (
          <div className="table-scroll">
            <table>
              <thead>
                <tr>
                  <th scope="col">Когда</th>
                  <th scope="col">Что</th>
                  <th scope="col">Кто</th>
                  <th scope="col">Результат</th>
                </tr>
              </thead>
              <tbody>
                {events.map((event) => (
                  <tr key={event.id}>
                    <td className="nowrap">{formatDateTime(event.createdAt)}</td>
                    <td title={event.action}>
                      {auditActionLabel(event.action)}
                      {detailsOf(event.action, event.details) ? <div className="muted text-sm">{detailsOf(event.action, event.details)}</div> : null}
                    </td>
                    <td>{event.actorLabel ?? '—'}</td>
                    <td>
                      {event.result === 'success' ? (
                        'успешно'
                      ) : (
                        <span className="tone-down">
                          ошибка{event.errorCode ? <span className="mono text-sm"> · {event.errorCode}</span> : null}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
      <LoadMore
        shown={events.length}
        hasMore={query.hasNextPage}
        busy={query.isFetchingNextPage}
        onMore={() => void query.fetchNextPage()}
      />
    </>
  );
}
